import React, { FC, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { IAccrualFutureBalanceDTO, ICreateAbsenceRequestDTO } from 'src/api/dtos';
import { useBalanceCheck } from 'src/features/absences/AbsenceRequestDrawer/hooks';
import {
  AbsenceBalanceDetailsSection,
  AbsenceBalanceIcon,
  AccrualBalanceSection,
} from 'src/features/absences/future-balance';
import { AccrualBalanceCheckButton } from 'src/features/absences/future-balance/AccrualBalanceCheckButton';
import { Modal } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';

interface Props {
  scheduledAbsence: IAccrualFutureBalanceDTO;
}

export const AccrualFutureBalanceSection: FC<Props> = ({ scheduledAbsence }) => {
  const { t } = useTranslate();
  const { settings } = useSelector((state: RootState) => state.user);
  const [showBreakdownDetails, setShowBreakdownDetails] = useState<boolean>(false);

  const inputs: Partial<ICreateAbsenceRequestDTO> = {
    isPartialDay: !!scheduledAbsence.startTime,
    absenceTypeId: scheduledAbsence.timeCategoryId,
    startDate: scheduledAbsence.startDate,
    endDate: scheduledAbsence.endDate,
    startTime: scheduledAbsence.startTime ?? undefined,
    endTime: scheduledAbsence.endTime ?? undefined,
  };

  const { balance, loading, checkBalance, cancel: cancelBalance } = useBalanceCheck({ inputs });

  const hasEmptyBalance = !balance?.currentBalanceInSeconds && !balance?.expectedBalanceInSeconds;

  return (
    <Stack direction="column" spacing={2}>
      <Typography fontWeight={600} variant="body1" color="primary">
        {t('accrualSummary.futureBalanceCalculation')}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        {balance && hasEmptyBalance && (
          <AbsenceBalanceIcon
            balance={balance}
            hideTooltip
            shouldShowError={!!settings?.isFutureBalanceViolationBlockEnabled}
            size={28}
          />
        )}
        {balance ? (
          !hasEmptyBalance && (
            <Stack direction="row">
              <AccrualBalanceSection
                balance={balance}
                isFutureBalanceCheckEnabled={!!settings?.isFutureBalanceCheckEnabled}
                showAccrualBalanceInDays={!!settings?.showAccrualBalanceInDays}
                isFutureBalanceViolationBlockEnabled={!!settings?.isFutureBalanceViolationBlockEnabled}
                onViewDetails={() => setShowBreakdownDetails(true)}
              />
              <Modal
                sx={{ minWidth: { sx: '100%', md: 780 } }}
                open={showBreakdownDetails}
                onClose={() => setShowBreakdownDetails(false)}
                modalHeaderProps={{
                  title: t('accrualSummary.futureBalanceCalculationBreakdown'),
                  onClose: () => setShowBreakdownDetails(false),
                }}
              >
                <AbsenceBalanceDetailsSection
                  balance={balance}
                  absence={inputs}
                  isFutureBalanceCheckEnabled={!!settings?.isFutureBalanceCheckEnabled}
                  isFutureBalanceViolationBlockEnabled={!!settings?.isFutureBalanceViolationBlockEnabled}
                  showAccrualBalanceInDays={!!settings?.showAccrualBalanceInDays}
                />
              </Modal>
            </Stack>
          )
        ) : (
          <AccrualBalanceCheckButton
            loading={loading}
            disabled={loading}
            label={t('accrualSummary.calculateFutureBalance')}
            onBalanceCheck={checkBalance}
            onBalanceCancel={cancelBalance}
          />
        )}
      </Stack>
    </Stack>
  );
};
